import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { Settings, Plus, Trash2, Save, Package, Droplets, AlertTriangle, X, Calendar, Scale } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// Default harvesting equipment
const DEFAULT_HARVESTING_EQUIPMENT = [
  { id: 'extractors', name: 'Honey Extractors', key: 'extractors', qty: 0, price: 0 },
  { id: 'uncapping_knives', name: 'Uncapping Knives', key: 'uncapping_knives', qty: 0, price: 0 },
  { id: 'uncapping_forks', name: 'Uncapping Forks', key: 'uncapping_forks', qty: 0, price: 0 },
  { id: 'uncapping_tanks', name: 'Uncapping Tanks', key: 'uncapping_tanks', qty: 0, price: 0 },
  { id: 'honey_buckets', name: 'Honey Buckets', key: 'honey_buckets', qty: 0, price: 0 },
  { id: 'strainers', name: 'Strainers/Filters', key: 'strainers', qty: 0, price: 0 },
  { id: 'bottling_tanks', name: 'Bottling Tanks', key: 'bottling_tanks', qty: 0, price: 0 },
  { id: 'bottling_equipment', name: 'Bottling Equipment', key: 'bottling_equipment', qty: 0, price: 0 },
  { id: 'honey_gates', name: 'Honey Gates', key: 'honey_gates', qty: 0, price: 0 },
  { id: 'refractometers', name: 'Refractometers', key: 'refractometers', qty: 0, price: 0 },
  { id: 'bee_escapes', name: 'Bee Escapes', key: 'bee_escapes', qty: 0, price: 0 },
  { id: 'fume_boards', name: 'Fume Boards', key: 'fume_boards', qty: 0, price: 0 },
  { id: 'capping_scratchers', name: 'Capping Scratchers', key: 'capping_scratchers', qty: 0, price: 0 },
  { id: 'honey_warmers', name: 'Honey Warmers', key: 'honey_warmers', qty: 0, price: 0 },
  { id: 'wax_melters', name: 'Wax Melters', key: 'wax_melters', qty: 0, price: 0 },
];

// Default container types
const DEFAULT_CONTAINER_TYPES = [
  { id: 'jar_500g', name: '500g Jar', capacity_kg: 0.5 },
  { id: 'jar_1kg', name: '1kg Jar', capacity_kg: 1 },
  { id: 'bucket_3kg', name: '3kg Bucket', capacity_kg: 3 },
  { id: 'bucket_5kg', name: '5kg Bucket', capacity_kg: 5 },
  { id: 'pail_20kg', name: '20kg Pail', capacity_kg: 20 },
  { id: 'drum_200kg', name: '200kg Drum', capacity_kg: 200 },
];

const HarvestSettingsView = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newEquipmentName, setNewEquipmentName] = useState('');
  const [newContainerName, setNewContainerName] = useState('');
  const [newContainerCapacity, setNewContainerCapacity] = useState('');
  const [newHoneyType, setNewHoneyType] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API_URL}/api/harvest-config`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        
        // Auto-seed defaults if missing
        let updated = false;
        if (!data.harvesting_equipment || data.harvesting_equipment.length === 0) {
          data.harvesting_equipment = DEFAULT_HARVESTING_EQUIPMENT;
          updated = true;
        }
        if (!data.container_types || data.container_types.length === 0) {
          data.container_types = DEFAULT_CONTAINER_TYPES;
          updated = true;
        }
        
        if (updated) {
          await saveConfigData(data);
        }
        
        setConfig(data);
      } else {
        // Create default config
        const defaultConfig = {
          harvesting_equipment: DEFAULT_HARVESTING_EQUIPMENT,
          container_types: DEFAULT_CONTAINER_TYPES,
          honey_types: ['Wildflower', 'Clover', 'Manuka', 'Bush', 'Mixed Floral']
        };
        await saveConfigData(defaultConfig);
        setConfig(defaultConfig);
      }
    } catch (err) {
      setError('Failed to load harvest settings');
    } finally {
      setLoading(false);
    }
  };

  const saveConfigData = async (data) => {
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
    await fetch(`${API_URL}/api/harvest-config`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data)
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await saveConfigData(config);
      toast.success('Harvest settings saved!');
    } catch (err) {
      setError('Failed to save settings');
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateEquipment = (id, field, value) => {
    setConfig(prev => ({
      ...prev,
      harvesting_equipment: prev.harvesting_equipment.map(eq =>
        eq.id === id ? { ...eq, [field]: field === 'qty' || field === 'price' ? parseFloat(value) || 0 : value } : eq
      )
    }));
  };

  const addCustomEquipment = () => {
    if (!newEquipmentName.trim()) return;
    const id = `custom_${Date.now()}`;
    setConfig(prev => ({
      ...prev,
      harvesting_equipment: [
        ...prev.harvesting_equipment,
        { id, name: newEquipmentName.trim(), key: id, qty: 0, price: 0, is_custom: true }
      ]
    }));
    setNewEquipmentName('');
    toast.success('Equipment added');
  };

  const removeEquipment = (id) => {
    setConfig(prev => ({
      ...prev,
      harvesting_equipment: prev.harvesting_equipment.filter(eq => eq.id !== id)
    }));
  };

  const updateContainer = (id, field, value) => {
    setConfig(prev => ({
      ...prev,
      container_types: prev.container_types.map(ct =>
        ct.id === id ? { ...ct, [field]: field === 'capacity_kg' ? parseFloat(value) || 0 : value } : ct
      )
    }));
  };

  const addCustomContainer = () => {
    if (!newContainerName.trim() || !newContainerCapacity) return;
    const id = `custom_${Date.now()}`;
    setConfig(prev => ({
      ...prev,
      container_types: [
        ...prev.container_types,
        { id, name: newContainerName.trim(), capacity_kg: parseFloat(newContainerCapacity) || 0, is_custom: true }
      ]
    }));
    setNewContainerName('');
    setNewContainerCapacity('');
    toast.success('Container added');
  };

  const removeContainer = (id) => {
    setConfig(prev => ({
      ...prev,
      container_types: prev.container_types.filter(ct => ct.id !== id)
    }));
  };

  // Calculate totals
  const totalEquipmentValue = config?.harvesting_equipment?.reduce((sum, eq) => 
    sum + ((eq.qty || 0) * (eq.price || 0)), 0) || 0;
  const totalEquipmentItems = config?.harvesting_equipment?.filter(eq => (eq.qty || 0) > 0).length || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-stone-500">Loading harvest settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0 max-w-full overflow-x-hidden" data-testid="harvest-settings-view">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Settings className="h-6 w-6 text-amber-600" />
            Harvest Settings
          </h2>
          <p className="text-stone-500 mt-1">Configure your harvesting equipment and containers</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          {error}
        </div>
      )}

      {/* Harvest Season Start */}
      <Card className="border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-amber-600" />
              <Label className="font-medium text-stone-700">Harvest Year Starts</Label>
            </div>
            <Select
              value={String(config?.harvest_year_start_month || 9)}
              onValueChange={(val) => setConfig({ ...config, harvest_year_start_month: parseInt(val) })}
            >
              <SelectTrigger className="w-44" data-testid="harvest-start-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-stone-500">
              Current season: {(() => {
                const sm = config?.harvest_year_start_month || 9;
                const now = new Date();
                const y = now.getMonth() + 1 >= sm ? now.getFullYear() : now.getFullYear() - 1;
                return `${MONTHS[sm - 1].label} ${y} – ${MONTHS[sm === 1 ? 11 : sm - 2].label} ${sm === 1 ? y : y + 1}`;
              })()}
            </span>
          </div>
          <div className="flex items-center gap-4 flex-wrap mt-3 pt-3 border-t border-amber-100">
            <div className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-amber-600" />
              <Label className="font-medium text-stone-700">Weight Unit</Label>
            </div>
            <span data-testid="harvest-unit-readonly" className="px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-sm font-semibold">
              Using {config?.default_unit === 'lbs' ? 'Pounds (lbs)' : 'Kilograms (kg)'}
            </span>
            <button type="button" onClick={() => navigate('/?view=weight-config&section=general')} data-testid="harvest-unit-change-link" className="text-sm text-amber-700 underline hover:text-amber-900">Change in Weight settings</button>
            <span className="text-sm text-stone-500 w-full">One unit for your whole account: hive weights, inspections, harvests, analytics and PDFs.</span>
          </div>
        </CardContent>
      </Card>

      {/* Equipment Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl mb-1">🔧</div>
            <div className="text-2xl font-bold text-orange-700">{totalEquipmentItems}</div>
            <div className="text-xs text-orange-600">Equipment Items</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl mb-1">💰</div>
            <div className="text-2xl font-bold text-green-700">${totalEquipmentValue.toFixed(2)}</div>
            <div className="text-xs text-green-600">Total Investment</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl mb-1">📦</div>
            <div className="text-2xl font-bold text-blue-700">{config?.container_types?.length || 0}</div>
            <div className="text-xs text-blue-600">Container Types</div>
          </CardContent>
        </Card>
      </div>

      {/* Harvesting Equipment */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-xl">🔧</span>
            Harvesting Equipment
          </CardTitle>
          <CardDescription>Track your harvesting equipment inventory and costs</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <th className="text-left py-2 px-3 font-medium text-stone-700">Equipment</th>
                  <th className="text-center py-2 px-3 font-medium text-stone-700 w-24">Quantity</th>
                  <th className="text-center py-2 px-3 font-medium text-stone-700 w-32">Unit Price ($)</th>
                  <th className="text-right py-2 px-3 font-medium text-stone-700 w-28">Subtotal</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {config?.harvesting_equipment?.map(equip => {
                  const subtotal = (equip.qty || 0) * (equip.price || 0);
                  return (
                    <tr key={equip.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-2 px-3 text-stone-700">
                        {equip.name}
                        {equip.is_custom && <span className="text-xs text-amber-500 ml-2">(Custom)</span>}
                      </td>
                      <td className="py-2 px-3">
                        <Input
                          type="number"
                          min="0"
                          value={equip.qty || 0}
                          onChange={(e) => updateEquipment(equip.id, 'qty', e.target.value)}
                          className="w-20 h-8 text-center mx-auto"
                        />
                      </td>
                      <td className="py-2 px-3">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={equip.price || 0}
                          onChange={(e) => updateEquipment(equip.id, 'price', e.target.value)}
                          className="w-28 h-8 text-center mx-auto"
                        />
                      </td>
                      <td className="py-2 px-3 text-right font-medium text-stone-700">
                        ${subtotal.toFixed(2)}
                      </td>
                      <td className="py-2 px-1">
                        {equip.is_custom && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeEquipment(equip.id)}
                            className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-orange-50">
                  <td colSpan="3" className="py-3 px-3 font-bold text-orange-800">Total Equipment Value</td>
                  <td className="py-3 px-3 text-right font-bold text-orange-800 text-lg">${totalEquipmentValue.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Add Custom Equipment */}
          <div className="flex gap-2 pt-2 border-t border-stone-200">
            <Input
              value={newEquipmentName}
              onChange={(e) => setNewEquipmentName(e.target.value)}
              placeholder="Add custom equipment..."
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && addCustomEquipment()}
            />
            <Button onClick={addCustomEquipment} variant="outline">
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Container Types */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            Container Types
          </CardTitle>
          <CardDescription>Define container sizes for honey storage and sales</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            {config?.container_types?.map(container => (
              <div 
                key={container.id} 
                className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-100"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">🍯</span>
                  <div>
                    <div className="font-medium text-stone-700">{container.name}</div>
                    <div className="text-xs text-stone-500">{container.capacity_kg}kg capacity</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={container.capacity_kg || 0}
                    onChange={(e) => updateContainer(container.id, 'capacity_kg', e.target.value)}
                    className="w-20 h-8 text-center"
                  />
                  <span className="text-sm text-stone-500">kg</span>
                  {container.is_custom && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeContainer(container.id)}
                      className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Add Custom Container */}
          <div className="flex gap-2 pt-2 border-t border-stone-200">
            <Input
              value={newContainerName}
              onChange={(e) => setNewContainerName(e.target.value)}
              placeholder="Container name..."
              className="flex-1"
            />
            <Input
              type="number"
              min="0"
              step="0.1"
              value={newContainerCapacity}
              onChange={(e) => setNewContainerCapacity(e.target.value)}
              placeholder="Capacity (kg)"
              className="w-32"
            />
            <Button onClick={addCustomContainer} variant="outline">
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Honey Types */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Droplets className="h-5 w-5 text-amber-600" />
            Honey Types
          </CardTitle>
          <CardDescription>Manage honey types for your harvests</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {config?.honey_types?.map((type, idx) => {
              // Handle both object and string formats
              const typeName = typeof type === 'string' ? type : type.name;
              const typeId = typeof type === 'object' ? type.id : idx;
              return (
                <span 
                  key={typeId} 
                  className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium flex items-center gap-2"
                >
                  🍯 {typeName}
                  <button
                    onClick={() => {
                      const newTypes = config.honey_types.filter((_, i) => i !== idx);
                      setConfig({ ...config, honey_types: newTypes });
                    }}
                    className="text-amber-600 hover:text-red-600 transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              );
            })}
          </div>
          
          {/* Add New Honey Type */}
          <div className="flex gap-2">
            <Input
              placeholder="New honey type (e.g., Eucalyptus, Lavender)"
              value={newHoneyType}
              onChange={(e) => setNewHoneyType(e.target.value)}
              className="flex-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newHoneyType.trim()) {
                  const currentTypes = config?.honey_types || [];
                  if (!currentTypes.includes(newHoneyType.trim())) {
                    setConfig({ ...config, honey_types: [...currentTypes, newHoneyType.trim()] });
                    setNewHoneyType('');
                  } else {
                    toast.error('This honey type already exists');
                  }
                }
              }}
            />
            <Button
              size="sm"
              onClick={() => {
                if (newHoneyType.trim()) {
                  const currentTypes = config?.honey_types || [];
                  if (!currentTypes.includes(newHoneyType.trim())) {
                    setConfig({ ...config, honey_types: [...currentTypes, newHoneyType.trim()] });
                    setNewHoneyType('');
                  } else {
                    toast.error('This honey type already exists');
                  }
                }
              }}
              disabled={!newHoneyType.trim()}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
          
          <p className="text-xs text-stone-500 mt-3">
            💡 Honey types are used when recording harvests to categorize your honey production.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default HarvestSettingsView;
